// googleDriveService.js
// This file handles all Google Drive API interactions
/* global gapi, google */
// Optional: only use an API key if provided via env; otherwise rely solely on OAuth token to avoid referrer blocks
const DRIVE_API_KEY = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY || null; // Move to environment variables
const DRIVE_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '303036808030-0c4233rc41sdt98qhccuiho80fgk96vs.apps.googleusercontent.com';
const DRIVE_APP_ID = process.env.REACT_APP_GOOGLE_PROJECT_NUMBER || '303036808030';
const DRIVE_SCOPES =
  'https://www.googleapis.com/auth/drive.appdata ' +
  'https://www.googleapis.com/auth/drive.file ' +
  'https://www.googleapis.com/auth/userinfo.email ' +
  'https://www.googleapis.com/auth/userinfo.profile ' +
  'openid';
const DRIVE_RECEIPT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif'
];
const DRIVE_PICKER_MIME_TYPES = DRIVE_RECEIPT_MIME_TYPES.join(',');
// Define both old and new filenames to support migration
const TRANSACTIONS_FILENAME = '70lives-ledger-transactions.json';
const DEPRECIATION_FILENAME = '70lives-ledger-depreciation.json';
const SERVICE_PRICES_FILENAME = '70lives-ledger-service-prices.json';
const COST_ITEMS_FILENAME = '70lives-ledger-cost-items.json';
const EXTRA_CATEGORIES_FILENAME = '70lives-ledger-extra-categories.json';
const GOALS_FILENAME = '70lives-ledger-goals.json';
const VAT_SETTINGS_FILENAME = '70lives-ledger-vat-settings.json';
const CLIENTS_FILENAME = '70lives-ledger-clients.json';
const BUSINESS_PROFILE_FILENAME = '70lives-ledger-business-profile.json';

// Legacy filenames (from previous app version)
const LEGACY_TRANSACTIONS_FILENAME = 'beauty-finance-tracker-transactions.json';
const LEGACY_DEPRECIATION_FILENAME = 'beauty-finance-tracker-depreciation.json';
const LEGACY_SERVICE_PRICES_FILENAME = 'beauty-finance-tracker-service-prices.json';
const LEGACY_COST_ITEMS_FILENAME = 'beauty-finance-tracker-cost-items.json';
const LEGACY_EXTRA_CATEGORIES_FILENAME = 'beauty-finance-tracker-extra-categories.json';
const LEGACY_GOALS_FILENAME = 'beauty-finance-tracker-goals.json';
const LEGACY_VAT_SETTINGS_FILENAME = 'beauty-finance-tracker-vat-settings.json';
const LEGACY_CLIENTS_FILENAME = 'beauty-finance-tracker-clients.json';
const LEGACY_BUSINESS_PROFILE_FILENAME = 'beauty-finance-tracker-business-profile.json';

// Track token and client initialization
let accessToken = null;
let tokenClient = null;
let gapiInited = false;
let gisInited = false;
let pickerInited = false;
let drivePermissionVerified = false;

const DRIVE_CONSENT_KEY = 'hasDriveConsent';
const DRIVE_AUTH_STATE_KEY = 'driveAuthState';

const getDriveApiErrorStatus = (error) => (
  Number(error?.status) ||
  Number(error?.result?.error?.code) ||
  Number(error?.statusCode) ||
  Number(error?.code) ||
  0
);

const markDrivePermissionGranted = () => {
  drivePermissionVerified = true;
  try { localStorage.setItem(DRIVE_CONSENT_KEY, 'true'); } catch {}
  try { localStorage.setItem(DRIVE_AUTH_STATE_KEY, 'authenticated'); } catch {}
  try { localStorage.setItem('setup_step_drive', 'true'); } catch {}
};

const markDrivePermissionMissing = (error = null) => {
  const status = getDriveApiErrorStatus(error);
  drivePermissionVerified = false;
  try { localStorage.removeItem(DRIVE_CONSENT_KEY); } catch {}
  try { localStorage.removeItem(DRIVE_AUTH_STATE_KEY); } catch {}
  try { localStorage.removeItem('setup_step_drive'); } catch {}

  if (status === 401) {
    try { localStorage.removeItem('google_access_token'); } catch {}
    accessToken = null;
  }
};

// Initialize the API client library
export const initGoogleDriveAPI = async () => {
  try {
    // Initialize the gapi.client
    if (!gapiInited) {
      console.log('Initializing GAPI');
      await new Promise((resolve, reject) => {
        gapi.load('client', {
          callback: resolve,
          onerror: reject
        });
      });
      
      const initConfig = {
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest']
      };
      if (DRIVE_API_KEY) {
        initConfig.apiKey = DRIVE_API_KEY;
      } else {
        console.log('Skipping Drive API key in gapi client init (using OAuth token only).');
      }

      await gapi.client.init(initConfig);
      
      gapiInited = true;
      console.log('GAPI initialized');
    }
    
    // Initialize the Google Identity Services
    if (!gisInited) {
  console.log('Initializing GIS');
  // Get the current origin for the redirect URI
  // For Vercel deployments, we need to be very explicit about the origin
  const origin = window.location.origin;
  
  // Select the appropriate redirect URI based on the domain
  let redirectUri;
  if (origin.includes('app.70lives.com')) {
    redirectUri = 'https://app.70lives.com/__/auth/handler';
  } else if (origin.includes('70lives.com')) {
    redirectUri = 'https://70lives.com/__/auth/handler';
  } else if (origin.includes('beautyfinancetracker')) {
    redirectUri = 'https://beautyfinancetracker.firebaseapp.com/__/auth/handler';
  } else {
    // Default to the current origin for local development
    redirectUri = origin;
  }
  
  console.log('Current origin:', origin);
  console.log('Setting redirect URI to:', redirectUri);
      
      // Log the full URL for debugging
      console.log('Full current URL:', window.location.href);
      
      tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: DRIVE_CLIENT_ID,
        scope: DRIVE_SCOPES,
        redirect_uri: redirectUri,
        callback: (tokenResponse) => {
          if (tokenResponse && tokenResponse.access_token) {
            accessToken = tokenResponse.access_token;
            // Store token as backup
            localStorage.setItem('google_access_token', accessToken);
            console.log('Token received and stored');
          }
        }
      });
      
      gisInited = true;
      console.log('GIS initialized');
    }
    
    console.log('Google Drive API initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing Google API:', error);
    return false;
  }
};

// Check if user is currently signed in
export const isSignedIn = () => {
  const token = accessToken || localStorage.getItem('google_access_token');
  const signedIn = !!token && drivePermissionVerified;
  console.log('isSignedIn check result:', signedIn);
  return signedIn;
};

// Sign in and request Drive permissions
export const signInWithGoogle = async () => {
  try {
    // Make sure API is initialized
    if (!gapiInited || !gisInited) {
      console.log('APIs not initialized yet, initializing...');
      await initGoogleDriveAPI();
    }
    
    console.log('Requesting access token');
    
    return new Promise((resolve, reject) => {
      // Store the current URL to handle redirect properly
      const currentUrl = window.location.href;
      const origin = window.location.origin;
      console.log('Current URL for redirect:', currentUrl);
      console.log('Origin for redirect:', origin);
      
      // Get a new token
      tokenClient.callback = async (response) => {
        if (response.error) {
          console.error('Error getting token:', response);
          reject(response.error);
          return;
        }
        
        accessToken = response.access_token;
        localStorage.setItem('google_access_token', accessToken);
        console.log('Access token obtained successfully');
        
        try {
          // Get user profile
          const userResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          });
          
          if (!userResponse.ok) {
            throw new Error('Failed to fetch user profile');
          }
          
          const userInfo = await userResponse.json();
          console.log('User profile fetched successfully');

          const driveAccessGranted = await verifyDriveAppDataPermission();
          if (!driveAccessGranted) {
            console.warn('Google sign-in succeeded but Drive app data permission was not granted.');
          }

          // Sync with Firebase Auth
          let firebaseUser = null;
          try {
            const { syncGoogleAuthWithFirebase } = await import('./utils/authSync');
            firebaseUser = await syncGoogleAuthWithFirebase(accessToken);
            console.log('Google auth synced with Firebase');
          } catch (syncError) {
            console.warn('Could not sync with Firebase, but Google auth succeeded:', syncError);
          }

          const googleUser = {
            id: userInfo.sub,
            name: userInfo.name,
            email: userInfo.email,
            imageUrl: userInfo.picture,
            accessToken: accessToken,
            firebaseUid: firebaseUser?.uid || null,
            driveAccessGranted
          };
          
          resolve(googleUser);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          reject(error);
        }
      };
      
      // Prompt the user to select an account on explicit sign-in
      // For explicit sign-in, it's okay to show the account picker
      try {
        console.log('Requesting access token with prompt');
        tokenClient.requestAccessToken({ 
          prompt: 'consent select_account'
        });
      } catch (error) {
  console.error('Error requesting access token:', error);
  reject(error);
}
    });
  } catch (error) {
    console.error('Google Sign-In Error:', error);
    throw error;
  }
};

// Try to restore session on page load
export const tryRestoreSession = async () => {
  try {
    // Check if we have a stored token
    const storedToken = localStorage.getItem('google_access_token');
    
    if (storedToken && window.gapi && window.gapi.client) {
      console.log('Found stored token, attempting to restore session');
      accessToken = storedToken;
      
      // Set the token on gapi client
      window.gapi.client.setToken({
        access_token: storedToken
      });
      
      const driveReady = await verifyDriveAppDataPermission();
      if (driveReady) {
        console.log('Session restored successfully');
        accessToken = storedToken; // Update your module's accessToken variable
        return true;
      }

      console.log('Stored token did not have verified Drive app data permission');
    }
    return false;
  } catch (error) {
    console.error('Failed to restore session:', error);
    const status = getDriveApiErrorStatus(error);
    if (status === 401 || status === 403) {
      markDrivePermissionMissing(error);
    }
    return false;
  }
};

// Sign out from Google
export const signOut = async () => {
  try {
    const tokenToRevoke = getAccessToken();
    if (tokenToRevoke) {
      // Revoke the token
      google.accounts.oauth2.revoke(tokenToRevoke, () => {
        console.log('Token revoked');
      });
    }

    accessToken = null;
    localStorage.removeItem('google_access_token');
    markDrivePermissionMissing();
    console.log('Signed out of Google successfully');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Get token for API calls
const getAccessToken = () => {
  return accessToken || localStorage.getItem('google_access_token');
};

const ensureDriveClientReady = async () => {
  if (gapi && gapi.client && gapi.client.drive && gapi.client.drive.files) {
    return true;
  }
  const initialized = await initGoogleDriveAPI();
  return !!(initialized && gapi && gapi.client && gapi.client.drive && gapi.client.drive.files);
};

const ensurePickerReady = async () => {
  const initialized = await initGoogleDriveAPI();
  if (!initialized || !window.gapi) {
    throw new Error('Google Drive client could not be initialized');
  }

  if (pickerInited && window.google?.picker) {
    return true;
  }

  await new Promise((resolve, reject) => {
    gapi.load('picker', {
      callback: resolve,
      onerror: reject
    });
  });

  pickerInited = true;
  return true;
};

const requestDrivePickerAccessToken = async () => {
  if (!tokenClient) {
    await initGoogleDriveAPI();
  }

  if (!tokenClient) {
    throw new Error('Google Drive sign-in is not ready');
  }

  return new Promise((resolve, reject) => {
    tokenClient.callback = async (response) => {
      if (!response || response.error) {
        reject(new Error(response?.error || 'Google Drive permission was not granted'));
        return;
      }

      accessToken = response.access_token;
      try { localStorage.setItem('google_access_token', accessToken); } catch {}
      await verifyDriveAppDataPermission();
      resolve(accessToken);
    };

    try {
      tokenClient.requestAccessToken({
        prompt: accessToken || localStorage.getItem('google_access_token') ? '' : 'consent select_account'
      });
    } catch (error) {
      reject(error);
    }
  });
};

const getDriveFileExtension = (name = '', mimeType = '') => {
  const extension = String(name || '').split('.').pop();
  if (extension && extension !== name) return extension.toLowerCase();

  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/heic') return 'heic';
  if (mimeType === 'image/heif') return 'heif';
  return 'jpg';
};

const isSupportedDriveReceiptMimeType = (mimeType = '') => (
  DRIVE_RECEIPT_MIME_TYPES.includes(String(mimeType || '').toLowerCase())
);

const normalizeDriveImportFileName = (file = {}) => {
  const rawName = String(file.name || file.title || 'drive-receipt').trim() || 'drive-receipt';
  const extension = getDriveFileExtension(rawName, file.mimeType);
  return rawName.includes('.') ? rawName : `${rawName}.${extension}`;
};

const escapeDriveQueryValue = (value = '') => String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");

export const downloadDriveFileAsFile = async (driveFile) => {
  const token = getAccessToken() || await requestDrivePickerAccessToken();
  const fileId = driveFile?.id;

  if (!fileId) {
    throw new Error('Missing Google Drive file ID');
  }

  const metadataResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=id,name,mimeType,size,modifiedTime,md5Checksum`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  if (!metadataResponse.ok) {
    throw new Error(`Could not read Drive file metadata (${metadataResponse.status})`);
  }

  const metadata = await metadataResponse.json();
  if (!isSupportedDriveReceiptMimeType(metadata.mimeType)) {
    throw new Error(`Unsupported Drive file type: ${metadata.mimeType || 'unknown'}`);
  }

  const mediaResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  if (!mediaResponse.ok) {
    throw new Error(`Could not download Drive file (${mediaResponse.status})`);
  }

  const blob = await mediaResponse.blob();
  const name = normalizeDriveImportFileName(metadata);
  const file = new File([blob], name, {
    type: metadata.mimeType || blob.type || 'application/octet-stream',
    lastModified: metadata.modifiedTime ? new Date(metadata.modifiedTime).getTime() : Date.now()
  });

  file.driveMetadata = {
    id: metadata.id,
    name,
    mimeType: metadata.mimeType,
    size: metadata.size || null,
    modifiedTime: metadata.modifiedTime || null,
    md5Checksum: metadata.md5Checksum || null,
    folderId: driveFile.folderId || null,
    folderName: driveFile.folderName || null
  };

  return file;
};

export const openDriveReceiptPicker = async ({ allowMultiple = true } = {}) => {
  await ensurePickerReady();
  const token = await requestDrivePickerAccessToken();

  return new Promise((resolve, reject) => {
    if (!window.google?.picker) {
      reject(new Error('Google Drive picker is not available'));
      return;
    }

    const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setMimeTypes(DRIVE_PICKER_MIME_TYPES)
      .setSelectFolderEnabled(false);

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(token)
      .setCallback((data) => {
        if (data.action === google.picker.Action.CANCEL) {
          resolve([]);
          return;
        }

        if (data.action === google.picker.Action.PICKED) {
          resolve((data.docs || []).map((doc) => ({
            id: doc.id,
            name: doc.name || doc.title || 'Drive receipt',
            mimeType: doc.mimeType,
            url: doc.url || ''
          })));
        }
      });

    if (DRIVE_API_KEY) {
      picker.setDeveloperKey(DRIVE_API_KEY);
    }

    if (DRIVE_APP_ID) {
      picker.setAppId(DRIVE_APP_ID);
    }

    if (allowMultiple) {
      picker.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    picker.build().setVisible(true);
  });
};

export const openDriveReceiptFolderPicker = async () => {
  await ensurePickerReady();
  const token = await requestDrivePickerAccessToken();

  return new Promise((resolve, reject) => {
    if (!window.google?.picker) {
      reject(new Error('Google Drive picker is not available'));
      return;
    }

    const folderViewId = google.picker.ViewId.FOLDERS || google.picker.ViewId.DOCS;
    const view = new google.picker.DocsView(folderViewId)
      .setMimeTypes('application/vnd.google-apps.folder')
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true);

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(token)
      .setCallback((data) => {
        if (data.action === google.picker.Action.CANCEL) {
          resolve(null);
          return;
        }

        if (data.action === google.picker.Action.PICKED) {
          const folder = (data.docs || [])[0];
          resolve(folder ? {
            id: folder.id,
            name: folder.name || folder.title || 'Google Drive folder',
            url: folder.url || ''
          } : null);
        }
      });

    if (DRIVE_API_KEY) {
      picker.setDeveloperKey(DRIVE_API_KEY);
    }

    if (DRIVE_APP_ID) {
      picker.setAppId(DRIVE_APP_ID);
    }

    picker.build().setVisible(true);
  });
};

export const listDriveReceiptFolderFiles = async (folderId, { pageSize = 50 } = {}) => {
  const token = getAccessToken() || await requestDrivePickerAccessToken();
  const normalizedFolderId = String(folderId || '').trim();

  if (!normalizedFolderId) {
    throw new Error('Select a Google Drive folder first');
  }

  const mimeQuery = DRIVE_RECEIPT_MIME_TYPES
    .map((mimeType) => `mimeType='${escapeDriveQueryValue(mimeType)}'`)
    .join(' or ');
  const query = `'${escapeDriveQueryValue(normalizedFolderId)}' in parents and trashed=false and (${mimeQuery})`;
  const files = [];
  let pageToken = '';

  do {
    const params = new URLSearchParams({
      q: query,
      fields: 'nextPageToken,files(id,name,mimeType,size,modifiedTime,md5Checksum,webViewLink)',
      orderBy: 'createdTime desc',
      pageSize: String(pageSize),
      supportsAllDrives: 'true',
      includeItemsFromAllDrives: 'true'
    });
    if (pageToken) {
      params.set('pageToken', pageToken);
    }

    const response = await fetch(`https://www.googleapis.com/drive/v3/files?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Could not list Drive folder files (${response.status})`);
    }

    const data = await response.json();
    files.push(...(data.files || []));
    pageToken = data.nextPageToken || '';
  } while (pageToken);

  return files
    .filter((file) => isSupportedDriveReceiptMimeType(file.mimeType))
    .map((file) => ({
      id: file.id,
      name: file.name || 'Drive receipt',
      mimeType: file.mimeType,
      size: file.size || null,
      modifiedTime: file.modifiedTime || null,
      md5Checksum: file.md5Checksum || null,
      url: file.webViewLink || '',
      folderId: normalizedFolderId
    }));
};

export const verifyDriveAppDataPermission = async () => {
  try {
    const token = getAccessToken();
    if (!token) {
      markDrivePermissionMissing();
      return false;
    }

    const clientReady = await ensureDriveClientReady();
    if (!clientReady) {
      markDrivePermissionMissing();
      return false;
    }

    await gapi.client.setToken({ access_token: token });
    const response = await gapi.client.drive.files.list({
      pageSize: 1,
      fields: 'files(id)',
      spaces: 'appDataFolder'
    });

    if (response.status === 200) {
      markDrivePermissionGranted();
      return true;
    }

    markDrivePermissionMissing();
    return false;
  } catch (error) {
    const status = getDriveApiErrorStatus(error);
    if (status === 401 || status === 403) {
      markDrivePermissionMissing(error);
      return false;
    }

    console.warn('Could not verify Google Drive app data permission:', error);
    return false;
  }
};

// Find a file in Drive's appData folder by name
const findFileInDrive = async (fileName) => {
  try {
    const clientReady = await ensureDriveClientReady();
    if (!clientReady) {
      throw new Error('Google Drive client not initialized');
    }

    const token = getAccessToken();
    console.log('findFileInDrive - Using token:', token ? 'Available' : 'Missing');
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    // Ensure token is set
    await gapi.client.setToken({ access_token: token });
    
    // Add additional debugging
    console.log(`Searching for file "${fileName}" in appDataFolder space`);
    
    try {
      const response = await gapi.client.drive.files.list({
        q: `name='${fileName}'`,  // Simplified query
        fields: 'files(id, name, modifiedTime)',
        spaces: 'appDataFolder'   // This is the specific space for app data
      });
      
      console.log('File search response received:', response.status);
      
      const files = response && response.result && response.result.files ? response.result.files : [];
      console.log(`Found ${files ? files.length : 0} files matching "${fileName}"`);
      
      if (files && files.length > 0) {
        console.log(`Found file: ${fileName}`, files[0]);
        return files[0];
      }
      
      console.log(`File not found: ${fileName}`);
      return null;
    } catch (apiError) {
      console.error('API error in findFileInDrive:', apiError);
      
      // Check if this is a scope issue
      if (apiError.result && apiError.result.error && 
          (apiError.result.error.status === 'PERMISSION_DENIED' || 
           apiError.result.error.status === 403)) {
        console.error('Permission denied. Check if app has appDataFolder scope.');
      }
      
      throw apiError;
    }
  } catch (error) {
    console.error('Error finding file in Drive:', error);
    
    // Handle token revocation
    const status = getDriveApiErrorStatus(error);
    if (status === 401 || status === 403) {
      console.log('Drive token is invalid or missing app data permission');
      markDrivePermissionMissing(error);
    }
    
    throw error;
  }
};

// Create a new file in Drive's appData folder
const createFileInDrive = async (fileName, content) => {
  try {
    const token = getAccessToken();
    console.log('createFileInDrive - Using token:', token ? 'Available' : 'Missing');
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    const fileMetadata = {
      name: fileName,
      parents: ['appDataFolder']
    };
    
    const contentBlob = new Blob([JSON.stringify(content)], { type: 'application/json' });
    
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(fileMetadata)], { type: 'application/json' }));
    form.append('file', contentBlob);
    
    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: form
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed API call: ${response.status} ${response.statusText} - ${errorText}`);
      error.status = response.status;
      throw error;
    }
    
    const data = await response.json();
    console.log('File created:', data);
    return data;
  } catch (error) {
    console.error('Error creating file in Drive:', error);
    const status = getDriveApiErrorStatus(error);
    if (status === 401 || status === 403) {
      markDrivePermissionMissing(error);
    }
    throw error;
  }
};

// Update an existing file in Drive
const updateFileInDrive = async (fileId, content) => {
  try {
    const token = getAccessToken();
    console.log('updateFileInDrive - Using token:', token ? 'Available' : 'Missing');
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    const contentBlob = new Blob([JSON.stringify(content)], { type: 'application/json' });
    
    const response = await fetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: contentBlob
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed API call: ${response.status} ${response.statusText} - ${errorText}`);
      error.status = response.status;
      throw error;
    }
    
    const data = await response.json();
    console.log('File updated:', data);
    return data;
  } catch (error) {
    console.error('Error updating file in Drive:', error);
    const status = getDriveApiErrorStatus(error);
    if (status === 401 || status === 403) {
      markDrivePermissionMissing(error);
    }
    throw error;
  }
};

// Read a file from Drive
const readFileFromDrive = async (fileId) => {
  try {
    const token = getAccessToken();
    console.log('readFileFromDrive - Using token:', token ? 'Available' : 'Missing');
    console.log('Attempting to read file with ID:', fileId);
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    // Use fetch with explicit cors mode
    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      mode: 'cors'  // Explicitly set CORS mode
    });
    
    if (!response.ok) {
      let errorDetails = '';
      try {
        const errorData = await response.json();
        errorDetails = JSON.stringify(errorData);
      } catch (e) {
        errorDetails = await response.text();
      }
      
      console.error(`Drive API Error (${response.status}):`, errorDetails);
      
      // Specific error handling
      if (response.status === 400) {
        const error = new Error('Bad request - check file ID format');
        error.status = response.status;
        throw error;
      } else if (response.status === 401) {
        const error = new Error('Authorization failed - please sign in again');
        error.status = response.status;
        throw error;
      } else if (response.status === 403) {
        const error = new Error('Permission denied - you may not have access to this file');
        error.status = response.status;
        throw error;
      } else if (response.status === 404) {
        const error = new Error('File not found - it may have been deleted');
        error.status = response.status;
        throw error;
      } else {
        const error = new Error(`Failed to read file: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
    }
    
    // Try to parse as JSON, but handle errors if it's not valid JSON
    try {
      const data = await response.json();
      console.log('File read successfully, data received');
      return data;
    } catch (parseError) {
      console.error('Error parsing file content as JSON:', parseError);
      throw new Error('The file content is not valid JSON');
    }
  } catch (error) {
    console.error('Error reading file from Drive:', error);
    const status = getDriveApiErrorStatus(error);
    if (status === 401 || status === 403) {
      markDrivePermissionMissing(error);
    }
    throw error;
  }
};

const normalizeTransactionList = (value) => (
  Array.isArray(value) ? value : []
);

const parseTransactionTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return 0;

  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isNaN(millis) ? 0 : millis;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const millis = new Date(value).getTime();
    return Number.isNaN(millis) ? 0 : millis;
  }

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const dateValue = value.toDate();
      if (dateValue instanceof Date) {
        const millis = dateValue.getTime();
        if (!Number.isNaN(millis)) return millis;
      }
    }

    const seconds = Number(value.seconds ?? value._seconds);
    const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds);
    if (Number.isFinite(seconds)) {
      return seconds * 1000 + (Number.isFinite(nanoseconds) ? Math.floor(nanoseconds / 1000000) : 0);
    }
  }

  const raw = String(value).trim();
  if (/^\d{10,13}$/.test(raw)) {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      return raw.length === 10 ? numeric * 1000 : numeric;
    }
  }

  const millis = new Date(raw).getTime();
  return Number.isNaN(millis) ? 0 : millis;
};

const getTransactionTimestamp = (transaction) => (
  parseTransactionTimestamp(transaction?.updatedAt || transaction?.createdAt || transaction?.date)
);

const getTransactionIdentity = (transaction) => {
  if (!transaction || typeof transaction !== 'object') return null;

  const id = transaction.id;
  if (id !== null && id !== undefined && id !== '') {
    return `id:${String(id)}`;
  }
  return null;
};

const mergeTransactionLists = (primaryTransactions, fallbackTransactions) => {
  const mergedByIdentity = new Map();
  const unmatchedTransactions = [];

  const addTransaction = (transaction) => {
    const identity = getTransactionIdentity(transaction);
    if (!identity) {
      unmatchedTransactions.push(transaction);
      return;
    }

    const existing = mergedByIdentity.get(identity);
    if (!existing) {
      mergedByIdentity.set(identity, transaction);
      return;
    }

    if (getTransactionTimestamp(transaction) >= getTransactionTimestamp(existing)) {
      mergedByIdentity.set(identity, transaction);
    }
  };

  normalizeTransactionList(primaryTransactions).forEach(addTransaction);
  normalizeTransactionList(fallbackTransactions).forEach(addTransaction);

  return [...mergedByIdentity.values(), ...unmatchedTransactions].sort(
    (left, right) => getTransactionTimestamp(right) - getTransactionTimestamp(left)
  );
};

// Save transactions to Google Drive
export const saveTransactionsToDrive = async (transactions) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }
  
  try {
    // Save transactions
    const transactionsFile = await findFileInDrive(TRANSACTIONS_FILENAME);
    
    if (transactionsFile) {
      // Update existing file
      await updateFileInDrive(transactionsFile.id, transactions);
      console.log('Transactions updated in Drive');
    } else {
      // Create new file
      await createFileInDrive(TRANSACTIONS_FILENAME, transactions);
      console.log('Transactions saved to Drive');
    }
  } catch (error) {
    console.error('Error saving transactions to Drive:', error);
    throw error;
  }
};

// Save depreciation periods to Google Drive
export const saveDepreciationToDrive = async (depreciationPeriods) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }
  
  try {
    // Save depreciation periods
    const depreciationFile = await findFileInDrive(DEPRECIATION_FILENAME);
    
    if (depreciationFile) {
      // Update existing file
      await updateFileInDrive(depreciationFile.id, depreciationPeriods);
      console.log('Depreciation periods updated in Drive');
    } else {
      // Create new file
      await createFileInDrive(DEPRECIATION_FILENAME, depreciationPeriods);
      console.log('Depreciation periods saved to Drive');
    }
  } catch (error) {
    console.error('Error saving depreciation periods to Drive:', error);
    throw error;
  }
};

// Load transactions from Google Drive
export const loadTransactionsFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }
  
  try {
    console.log('Attempting to load transactions from Drive');

    const transactionsFile = await findFileInDrive(TRANSACTIONS_FILENAME);
    const legacyTransactionsFile = await findFileInDrive(LEGACY_TRANSACTIONS_FILENAME);

    let currentTransactions = [];
    let legacyTransactions = [];

    if (transactionsFile) {
      console.log('Found current transactions file, reading content');
      try {
        currentTransactions = normalizeTransactionList(await readFileFromDrive(transactionsFile.id));
      } catch (readError) {
        console.error('Error reading current transactions file:', readError);
        if (!legacyTransactionsFile) {
          throw readError;
        }
      }
    }

    if (legacyTransactionsFile) {
      console.log('Found legacy transactions file, reading content');
      try {
        legacyTransactions = normalizeTransactionList(await readFileFromDrive(legacyTransactionsFile.id));
      } catch (readError) {
        console.error('Error reading legacy transactions file:', readError);
        if (!transactionsFile || currentTransactions.length === 0) {
          throw readError;
        }
      }
    }

    const mergedTransactions = mergeTransactionLists(currentTransactions, legacyTransactions);
    const shouldMigrateLegacyTransactions = (
      legacyTransactions.length > 0 &&
      JSON.stringify(mergedTransactions) !== JSON.stringify(currentTransactions)
    );

    if (shouldMigrateLegacyTransactions) {
      try {
        console.log('Migrating legacy transactions into the current Drive file');
        if (transactionsFile) {
          await updateFileInDrive(transactionsFile.id, mergedTransactions);
        } else {
          await createFileInDrive(TRANSACTIONS_FILENAME, mergedTransactions);
        }
      } catch (migrationError) {
        console.warn('Failed to migrate legacy transactions to the current Drive file:', migrationError);
      }
    }

    if (transactionsFile || legacyTransactionsFile) {
      console.log('Transactions loaded from Drive successfully');
      try {
        localStorage.setItem('transactions_backup', JSON.stringify(mergedTransactions));
      } catch (backupError) {
        console.warn('Failed to create local backup:', backupError);
      }
      return mergedTransactions;
    }

    console.log('No transactions file found, creating an empty current transactions file');

    try {
      await createFileInDrive(TRANSACTIONS_FILENAME, []);
    } catch (createError) {
      console.warn('Failed to create initial file:', createError);
    }

    return [];
  } catch (error) {
    console.error('Error loading transactions from Drive:', error);
    // Try to recover from backup
    const backup = localStorage.getItem('transactions_backup');
    if (backup) {
      try {
        console.log('Recovering from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse backup data:', parseError);
      }
    }
    
    throw error;
  }
};

// Load depreciation periods from Google Drive
export const loadDepreciationFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }
  
  try {
    // Find depreciation file
    const depreciationFile = await findFileInDrive(DEPRECIATION_FILENAME);
    
    if (depreciationFile) {
      // Read file content
      const depreciationPeriods = await readFileFromDrive(depreciationFile.id);
      console.log('Depreciation periods loaded from Drive');
      return depreciationPeriods;
    }
    
    console.log('No depreciation periods file found');
    return {};
  } catch (error) {
    console.error('Error loading depreciation periods from Drive:', error);
    throw error;
  }
};

// Get current user info
export const getCurrentUser = async () => {
  try {
    if (!isSignedIn()) return null;
    
    const token = getAccessToken();
    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }
    
    const userInfo = await response.json();
    
    return {
      id: userInfo.sub,
      name: userInfo.name,
      email: userInfo.email,
      imageUrl: userInfo.picture,
      accessToken: token
    };
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};
// Store tokens in localStorage
export const storeAuthTokens = (tokens) => {
  localStorage.setItem('gdriveAuthTokens', JSON.stringify(tokens));
};
// Load service prices from Google Drive
export const loadServicePricesFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }
  
  try {
    console.log('Attempting to load service prices from Drive');
    
    // Find service prices file
    const servicePricesFile = await findFileInDrive(SERVICE_PRICES_FILENAME);
    
    if (servicePricesFile) {
      console.log('Found service prices file, reading content');
      
      try {
        // Read file content
        const servicePrices = await readFileFromDrive(servicePricesFile.id);
        console.log('Service prices loaded from Drive successfully');
        // Create a local backup
        try {
          localStorage.setItem('service_prices_backup', JSON.stringify(servicePrices));
        } catch (backupError) {
          console.warn('Failed to create local backup for service prices:', backupError);
        }
        
        return servicePrices;
      } catch (readError) {
        console.error('Error reading service prices file:', readError);
        throw readError;
      }
    }
    
    console.log('No service prices file found, returning empty object');
    
    // For new users, initialize with empty object
    // Consider creating the file here for new users
    try {
      console.log('Creating initial service prices file for new user');
      await createFileInDrive(SERVICE_PRICES_FILENAME, {});
    } catch (createError) {
      console.warn('Failed to create initial service prices file:', createError);
      // Continue even if this fails
    }
    
    return {};
  } catch (error) {
    console.error('Error loading service prices from Drive:', error);
    // Try to recover from backup
    const backup = localStorage.getItem('service_prices_backup');
    if (backup) {
      try {
        console.log('Recovering service prices from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse service prices backup data:', parseError);
      }
    }
    
    throw error;
  }
};

// Save service prices to Google Drive
export const saveServicePricesToDrive = async (servicePrices) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }
  
  try {
    // Save service prices
    const servicePricesFile = await findFileInDrive(SERVICE_PRICES_FILENAME);
    
    if (servicePricesFile) {
      // Update existing file
      await updateFileInDrive(servicePricesFile.id, servicePrices);
      console.log('Service prices updated in Drive');
    } else {
      // Create new file
      await createFileInDrive(SERVICE_PRICES_FILENAME, servicePrices);
      console.log('Service prices saved to Drive');
    }
    
    // Create a local backup
    try {
      localStorage.setItem('service_prices_backup', JSON.stringify(servicePrices));
    } catch (backupError) {
      console.warn('Failed to create local backup for service prices:', backupError);
    }
    
    return true;
  } catch (error) {
    console.error('Error saving service prices to Drive:', error);
    throw error;
  }
};

// Load cost items from Google Drive
export const loadCostItemsFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }

  try {
    console.log('Attempting to load cost items from Drive');

    let costItemsFile = await findFileInDrive(COST_ITEMS_FILENAME);
    if (!costItemsFile) {
      costItemsFile = await findFileInDrive(LEGACY_COST_ITEMS_FILENAME);
    }

    if (costItemsFile) {
      console.log('Found cost items file, reading content');

      try {
        const costItems = await readFileFromDrive(costItemsFile.id);
        console.log('Cost items loaded from Drive successfully');
        try {
          localStorage.setItem('cost_items_backup', JSON.stringify(costItems));
        } catch (backupError) {
          console.warn('Failed to create local backup for cost items:', backupError);
        }
        return costItems;
      } catch (readError) {
        console.error('Error reading cost items file:', readError);
        throw readError;
      }
    }

    console.log('No cost items file found, returning empty array');

    try {
      console.log('Creating initial cost items file for new user');
      await createFileInDrive(COST_ITEMS_FILENAME, []);
    } catch (createError) {
      console.warn('Failed to create initial cost items file:', createError);
    }

    return [];
  } catch (error) {
    console.error('Error loading cost items from Drive:', error);
    const backup = localStorage.getItem('cost_items_backup');
    if (backup) {
      try {
        console.log('Recovering cost items from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse cost items backup data:', parseError);
      }
    }

    throw error;
  }
};

// Save cost items to Google Drive
export const saveCostItemsToDrive = async (costItems) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }

  try {
    const costItemsFile = await findFileInDrive(COST_ITEMS_FILENAME);

    if (costItemsFile) {
      await updateFileInDrive(costItemsFile.id, costItems);
      console.log('Cost items updated in Drive');
    } else {
      await createFileInDrive(COST_ITEMS_FILENAME, costItems);
      console.log('Cost items saved to Drive');
    }

    try {
      localStorage.setItem('cost_items_backup', JSON.stringify(costItems));
    } catch (backupError) {
      console.warn('Failed to create local backup for cost items:', backupError);
    }

    return true;
  } catch (error) {
    console.error('Error saving cost items to Drive:', error);
    throw error;
  }
};

// Load clients from Google Drive
export const loadClientsFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }

  try {
    console.log('Attempting to load clients from Drive');

    let clientsFile = await findFileInDrive(CLIENTS_FILENAME);
    if (!clientsFile) {
      clientsFile = await findFileInDrive(LEGACY_CLIENTS_FILENAME);
    }

    if (clientsFile) {
      console.log('Found clients file, reading content');

      try {
        const clients = await readFileFromDrive(clientsFile.id);
        console.log('Clients loaded from Drive successfully');
        try {
          localStorage.setItem('clients_backup', JSON.stringify(clients));
        } catch (backupError) {
          console.warn('Failed to create local backup for clients:', backupError);
        }
        return clients;
      } catch (readError) {
        console.error('Error reading clients file:', readError);
        throw readError;
      }
    }

    console.log('No clients file found, returning empty array');

    try {
      console.log('Creating initial clients file for new user');
      await createFileInDrive(CLIENTS_FILENAME, []);
    } catch (createError) {
      console.warn('Failed to create initial clients file:', createError);
    }

    return [];
  } catch (error) {
    console.error('Error loading clients from Drive:', error);
    const backup = localStorage.getItem('clients_backup');
    if (backup) {
      try {
        console.log('Recovering clients from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse clients backup data:', parseError);
      }
    }

    throw error;
  }
};

// Save clients to Google Drive
export const saveClientsToDrive = async (clients) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }

  try {
    let clientsFile = await findFileInDrive(CLIENTS_FILENAME);
    if (!clientsFile) {
      clientsFile = await findFileInDrive(LEGACY_CLIENTS_FILENAME);
    }

    if (clientsFile) {
      await updateFileInDrive(clientsFile.id, clients);
      console.log('Clients updated in Drive');
    } else {
      await createFileInDrive(CLIENTS_FILENAME, clients);
      console.log('Clients saved to Drive');
    }

    try {
      localStorage.setItem('clients_backup', JSON.stringify(clients));
    } catch (backupError) {
      console.warn('Failed to create local backup for clients:', backupError);
    }

    return true;
  } catch (error) {
    console.error('Error saving clients to Drive:', error);
    throw error;
  }
};

// Load business profile settings from Google Drive
export const loadBusinessProfileFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }

  try {
    console.log('Attempting to load business profile from Drive');

    let businessProfileFile = await findFileInDrive(BUSINESS_PROFILE_FILENAME);
    if (!businessProfileFile) {
      businessProfileFile = await findFileInDrive(LEGACY_BUSINESS_PROFILE_FILENAME);
    }

    if (businessProfileFile) {
      const businessProfile = await readFileFromDrive(businessProfileFile.id);
      console.log('Business profile loaded from Drive successfully');
      try {
        localStorage.setItem('business_profile_backup', JSON.stringify(businessProfile));
      } catch (backupError) {
        console.warn('Failed to create local backup for business profile:', backupError);
      }
      return businessProfile;
    }

    console.log('No business profile file found, returning empty object');
    try {
      await createFileInDrive(BUSINESS_PROFILE_FILENAME, {});
    } catch (createError) {
      console.warn('Failed to create initial business profile file:', createError);
    }

    return {};
  } catch (error) {
    console.error('Error loading business profile from Drive:', error);
    const backup = localStorage.getItem('business_profile_backup');
    if (backup) {
      try {
        console.log('Recovering business profile from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse business profile backup data:', parseError);
      }
    }

    throw error;
  }
};

// Save business profile settings to Google Drive
export const saveBusinessProfileToDrive = async (businessProfile) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }

  try {
    let businessProfileFile = await findFileInDrive(BUSINESS_PROFILE_FILENAME);
    if (!businessProfileFile) {
      businessProfileFile = await findFileInDrive(LEGACY_BUSINESS_PROFILE_FILENAME);
    }

    if (businessProfileFile) {
      await updateFileInDrive(businessProfileFile.id, businessProfile);
      console.log('Business profile updated in Drive');
    } else {
      await createFileInDrive(BUSINESS_PROFILE_FILENAME, businessProfile);
      console.log('Business profile saved to Drive');
    }

    try {
      localStorage.setItem('business_profile_backup', JSON.stringify(businessProfile));
    } catch (backupError) {
      console.warn('Failed to create local backup for business profile:', backupError);
    }

    return true;
  } catch (error) {
    console.error('Error saving business profile to Drive:', error);
    throw error;
  }
};

// Load income goals from Google Drive
export const loadGoalsFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }

  try {
    console.log('Attempting to load goals from Drive');

    let goalsFile = await findFileInDrive(GOALS_FILENAME);
    if (!goalsFile) {
      goalsFile = await findFileInDrive(LEGACY_GOALS_FILENAME);
    }

    if (goalsFile) {
      console.log('Found goals file, reading content');

      try {
        const goals = await readFileFromDrive(goalsFile.id);
        console.log('Goals loaded from Drive successfully');
        try {
          localStorage.setItem('goals_backup', JSON.stringify(goals));
        } catch (backupError) {
          console.warn('Failed to create local backup for goals:', backupError);
        }
        return goals;
      } catch (readError) {
        console.error('Error reading goals file:', readError);
        throw readError;
      }
    }

    console.log('No goals file found, returning defaults');

    try {
      console.log('Creating initial goals file for new user');
      await createFileInDrive(GOALS_FILENAME, { amount: '', period: 'weekly', workDaysPerWeek: 5, workHoursPerWeek: 30, goalHourly: null });
    } catch (createError) {
      console.warn('Failed to create initial goals file:', createError);
    }

    return { amount: '', period: 'weekly', workDaysPerWeek: 5, workHoursPerWeek: 30, goalHourly: null };
  } catch (error) {
    console.error('Error loading goals from Drive:', error);
    const backup = localStorage.getItem('goals_backup');
    if (backup) {
      try {
        console.log('Recovering goals from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse goals backup data:', parseError);
      }
    }

    throw error;
  }
};

// Save income goals to Google Drive
export const saveGoalsToDrive = async (goals) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }

  try {
    let goalsFile = await findFileInDrive(GOALS_FILENAME);
    if (!goalsFile) {
      goalsFile = await findFileInDrive(LEGACY_GOALS_FILENAME);
    }

    if (goalsFile) {
      await updateFileInDrive(goalsFile.id, goals);
      console.log('Goals updated in Drive');
    } else {
      await createFileInDrive(GOALS_FILENAME, goals);
      console.log('Goals saved to Drive');
    }

    try {
      localStorage.setItem('goals_backup', JSON.stringify(goals));
    } catch (backupError) {
      console.warn('Failed to create local backup for goals:', backupError);
    }

    return true;
  } catch (error) {
    console.error('Error saving goals to Drive:', error);
    throw error;
  }
};

// Load VAT settings from Google Drive
export const loadVatSettingsFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }

  try {
    console.log('Attempting to load VAT settings from Drive');

    let vatSettingsFile = await findFileInDrive(VAT_SETTINGS_FILENAME);
    if (!vatSettingsFile) {
      vatSettingsFile = await findFileInDrive(LEGACY_VAT_SETTINGS_FILENAME);
    }

    if (vatSettingsFile) {
      console.log('Found VAT settings file, reading content');

      try {
        const vatSettings = await readFileFromDrive(vatSettingsFile.id);
        console.log('VAT settings loaded from Drive successfully');
        try {
          localStorage.setItem('vat_settings_backup', JSON.stringify(vatSettings));
        } catch (backupError) {
          console.warn('Failed to create local backup for VAT settings:', backupError);
        }
        return vatSettings;
      } catch (readError) {
        console.error('Error reading VAT settings file:', readError);
        throw readError;
      }
    }

    console.log('No VAT settings file found, returning empty object');

    try {
      console.log('Creating initial VAT settings file for new user');
      await createFileInDrive(VAT_SETTINGS_FILENAME, {});
    } catch (createError) {
      console.warn('Failed to create initial VAT settings file:', createError);
    }

    return {};
  } catch (error) {
    console.error('Error loading VAT settings from Drive:', error);
    const backup = localStorage.getItem('vat_settings_backup');
    if (backup) {
      try {
        console.log('Recovering VAT settings from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse VAT settings backup data:', parseError);
      }
    }

    throw error;
  }
};

// Save VAT settings to Google Drive
export const saveVatSettingsToDrive = async (vatSettings) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }

  try {
    let vatSettingsFile = await findFileInDrive(VAT_SETTINGS_FILENAME);
    if (!vatSettingsFile) {
      vatSettingsFile = await findFileInDrive(LEGACY_VAT_SETTINGS_FILENAME);
    }

    if (vatSettingsFile) {
      await updateFileInDrive(vatSettingsFile.id, vatSettings);
      console.log('VAT settings updated in Drive');
    } else {
      await createFileInDrive(VAT_SETTINGS_FILENAME, vatSettings);
      console.log('VAT settings saved to Drive');
    }

    try {
      localStorage.setItem('vat_settings_backup', JSON.stringify(vatSettings));
    } catch (backupError) {
      console.warn('Failed to create local backup for VAT settings:', backupError);
    }

    return true;
  } catch (error) {
    console.error('Error saving VAT settings to Drive:', error);
    throw error;
  }
};

// Load custom categories from Google Drive
export const loadExtraCategoriesFromDrive = async () => {
  if (!isSignedIn()) {
    console.error('User must be signed in to load data');
    throw new Error('User not signed in');
  }

  try {
    console.log('Attempting to load extra categories from Drive');

    let extraCategoriesFile = await findFileInDrive(EXTRA_CATEGORIES_FILENAME);
    if (!extraCategoriesFile) {
      extraCategoriesFile = await findFileInDrive(LEGACY_EXTRA_CATEGORIES_FILENAME);
    }

    if (extraCategoriesFile) {
      console.log('Found extra categories file, reading content');

      try {
        const extraCategories = await readFileFromDrive(extraCategoriesFile.id);
        console.log('Extra categories loaded from Drive successfully');
        try {
          localStorage.setItem('extra_categories_backup', JSON.stringify(extraCategories));
        } catch (backupError) {
          console.warn('Failed to create local backup for extra categories:', backupError);
        }
        return extraCategories;
      } catch (readError) {
        console.error('Error reading extra categories file:', readError);
        throw readError;
      }
    }

    console.log('No extra categories file found, returning defaults');

    try {
      console.log('Creating initial extra categories file for new user');
      await createFileInDrive(EXTRA_CATEGORIES_FILENAME, { income: [], expenses: [] });
    } catch (createError) {
      console.warn('Failed to create initial extra categories file:', createError);
    }

    return { income: [], expenses: [] };
  } catch (error) {
    console.error('Error loading extra categories from Drive:', error);
    const backup = localStorage.getItem('extra_categories_backup');
    if (backup) {
      try {
        console.log('Recovering extra categories from local backup');
        return JSON.parse(backup);
      } catch (parseError) {
        console.error('Failed to parse extra categories backup data:', parseError);
      }
    }

    throw error;
  }
};

// Save custom categories to Google Drive
export const saveExtraCategoriesToDrive = async (extraCategories) => {
  if (!isSignedIn()) {
    console.error('User must be signed in to save data');
    throw new Error('User not signed in');
  }

  try {
    let extraCategoriesFile = await findFileInDrive(EXTRA_CATEGORIES_FILENAME);
    if (!extraCategoriesFile) {
      extraCategoriesFile = await findFileInDrive(LEGACY_EXTRA_CATEGORIES_FILENAME);
    }

    if (extraCategoriesFile) {
      await updateFileInDrive(extraCategoriesFile.id, extraCategories);
      console.log('Extra categories updated in Drive');
    } else {
      await createFileInDrive(EXTRA_CATEGORIES_FILENAME, extraCategories);
      console.log('Extra categories saved to Drive');
    }

    try {
      localStorage.setItem('extra_categories_backup', JSON.stringify(extraCategories));
    } catch (backupError) {
      console.warn('Failed to create local backup for extra categories:', backupError);
    }

    return true;
  } catch (error) {
    console.error('Error saving extra categories to Drive:', error);
    throw error;
  }
};

// Initialize drive service
export const initDriveService = async () => {
  try {
    console.log('Starting drive service initialization');
    const result = await initGoogleDriveAPI();
    console.log('Drive service initialized successfully');
     // Add this section to restore the session if possible
     if (result) {
      console.log('Attempting to restore previous session');
      const sessionRestored = await tryRestoreSession();
      if (sessionRestored) {
        console.log('Previous session successfully restored');
      } else {
        console.log('No previous session to restore or restoration failed');
      }
    }
    return result;
  } catch (error) {
    console.error('Failed to initialize drive service:', error);
    console.error('Initialization error details:', error);
    return false;
  }
};

// Ensure we have a usable Drive session without prompting the user.
// Tries in order: already signed in -> restore stored session -> silent token request
export const ensureDriveSession = async () => {
  try {
    const inited = await initGoogleDriveAPI();
    if (!inited) return false;

    if (isSignedIn()) return true;

    const restored = await tryRestoreSession();
    if (restored) return true;

    // If the user previously consented, try a SILENT token refresh (no UI).
    const hasPriorConsent = localStorage.getItem('hasDriveConsent') === 'true';
    if (!hasPriorConsent) return false;

    if (!tokenClient) return false;

    const silentOk = await new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      const timeoutId = setTimeout(() => finish(false), 5000);

      try {
        tokenClient.callback = async (response) => {
          clearTimeout(timeoutId);
          if (response && response.access_token) {
            accessToken = response.access_token;
            try { localStorage.setItem('google_access_token', accessToken); } catch {}
            const driveReady = await verifyDriveAppDataPermission();
            finish(driveReady);
          } else {
            finish(false);
          }
        };
        // Explicitly request non-interactive retrieval to avoid popup flow on refresh.
        tokenClient.requestAccessToken({ prompt: 'none' });
      } catch (e) {
        clearTimeout(timeoutId);
        finish(false);
      }
    });

    return !!silentOk;
  } catch (e) {
    console.error('ensureDriveSession failed:', e);
    return false;
  }
};
